import { useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>("");

  const selectedCategory = categories.find((c) => c.key === selected);

  return (
    <section id="categories" className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-2xl text-foreground-950 md:text-4xl">{t("categories.title")}</h2>
        <p className="mt-3 text-base text-foreground-600">{t("categories.subtitle")}</p>
      </div>

      <div className="mx-auto mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-full border border-background-300 bg-background-50 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className={selected ? "font-semibold" : "text-foreground-500"}>
              {selectedCategory ? t(selectedCategory.key) : t("categories.placeholder")}
            </span>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`text-base ${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} aria-hidden="true"></i>
            </span>
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true"></div>
              <div
                className="absolute left-0 right-0 z-20 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-background-200 bg-background-50 py-2"
                role="listbox"
              >
                {categories.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="option"
                    aria-selected={c.key === selected}
                    onClick={() => {
                      setSelected(c.key);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm transition-colors hover:bg-background-100 ${
                      c.key === selected ? "font-bold text-primary-700" : "text-foreground-700"
                    }`}
                  >
                    <img
                      src={c.img}
                      alt={t(c.key)}
                      className="h-8 w-8 rounded-lg object-cover object-top"
                    />
                    {t(c.key)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <a
          href={selectedCategory ? selectedCategory.url : "#"}
          onClick={(e) => {
            if (!selectedCategory) e.preventDefault();
          }}
          target="_blank"
          rel="noopener nofollow"
          className={`whitespace-nowrap rounded-full px-6 py-3 text-center text-sm font-bold transition-colors ${
            selectedCategory
              ? "bg-accent-500 text-background-50 hover:bg-accent-600"
              : "cursor-not-allowed bg-background-200 text-foreground-400"
          }`}
        >
          {t("categories.go")}
        </a>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {categories.map((c) => (
          <a
            key={c.key}
            href={c.url}
            target="_blank"
            rel="noopener nofollow"
            className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-transform hover:-translate-y-1"
          >
            <div className="h-28 w-full overflow-hidden">
              <img
                src={c.img}
                alt={t(c.key)}
                title={`${t(c.key)} — USFans Spreadsheet`}
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex items-center justify-between px-3 py-3">
              <span className="whitespace-nowrap text-sm font-semibold text-foreground-800">{t(c.key)}</span>
              <span className="w-4 h-4 flex items-center justify-center text-foreground-400 transition-colors group-hover:text-primary-600">
                <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}